import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { 
  Shield, AlertTriangle, Info, Zap, 
  Users, Eye, CreditCard, CheckCircle,
  MapPin, Loader2, Search, TrendingUp
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface BusinessRule {
  id: string;
  category: 'plan' | 'validation' | 'visibility' | 'interaction' | 'permission' | 'profile' | 'location';
  rule: string;
  description: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  affects: string[];
}

interface BusinessRulesData {
  success: boolean;
  rules: BusinessRule[];
  metadata: {
    totalRules: number;
    timestamp: string;
  };
}

const categoryConfig = {
  plan: { label: 'Planos', icon: CreditCard, color: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/20' },
  validation: { label: 'Validação', icon: CheckCircle, color: 'bg-green-500/10 text-green-700 dark:text-green-300 border-green-500/20' },
  visibility: { label: 'Visibilidade', icon: Eye, color: 'bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/20' },
  interaction: { label: 'Interação', icon: Users, color: 'bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-500/20' },
  permission: { label: 'Permissões', icon: Shield, color: 'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/20' },
  profile: { label: 'Perfil', icon: Users, color: 'bg-pink-500/10 text-pink-700 dark:text-pink-300 border-pink-500/20' },
  location: { label: 'Localização', icon: MapPin, color: 'bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-500/20' },
};

const priorityConfig = {
  critical: { label: 'Crítica', icon: AlertTriangle, color: 'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/20' },
  high: { label: 'Alta', icon: Zap, color: 'bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-500/20' },
  medium: { label: 'Média', icon: Info, color: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/20' },
  low: { label: 'Baixa', icon: Info, color: 'bg-gray-500/10 text-gray-700 dark:text-gray-300 border-gray-500/20' },
};

const BusinessRulesMap: React.FC = () => {
  const [rules, setRules] = useState<BusinessRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedPriority, setSelectedPriority] = useState<string>('all');
  const { toast } = useToast();

  useEffect(() => {
    fetchBusinessRules();
  }, []);

  const fetchBusinessRules = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('analyze-business-rules');

      if (error) {
        console.error('Error fetching business rules:', error);
        toast({
          title: 'Erro ao carregar regras',
          description: 'Não foi possível carregar as regras de negócio.',
          variant: 'destructive'
        });
        return;
      }

      const rulesData = data as BusinessRulesData;
      if (rulesData.success) {
        setRules(rulesData.rules);
      } else {
        toast({
          title: 'Erro ao processar regras',
          description: 'As regras foram carregadas mas não puderam ser processadas.',
          variant: 'destructive'
        });
      }
    } catch (err) {
      console.error('Exception fetching business rules:', err);
      toast({
        title: 'Erro inesperado',
        description: 'Ocorreu um erro ao tentar carregar as regras.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredRules = rules.filter(rule => {
    const matchesSearch = searchTerm === '' || 
      rule.rule.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rule.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rule.affects.some(a => a.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'all' || rule.category === selectedCategory;
    const matchesPriority = selectedPriority === 'all' || rule.priority === selectedPriority;

    return matchesSearch && matchesCategory && matchesPriority;
  });

  const stats = {
    total: rules.length,
    byPriority: {
      critical: rules.filter(r => r.priority === 'critical').length,
      high: rules.filter(r => r.priority === 'high').length,
      medium: rules.filter(r => r.priority === 'medium').length,
      low: rules.filter(r => r.priority === 'low').length,
    },
    byCategory: Object.keys(categoryConfig).reduce((acc, cat) => {
      acc[cat] = rules.filter(r => r.category === cat).length;
      return acc;
    }, {} as Record<string, number>)
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-100">Regras de Negócio</h1>
          <p className="text-gray-400 mt-2">
            Carregando todas as regras de negócio do aplicativo...
          </p>
        </div>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-3 text-gray-400">Analisando Business Rules Engine...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-100">Regras de Negócio</h1>
        <p className="text-gray-400 mt-2">
          Documentação automática de todas as regras de negócio do aplicativo
        </p>
      </div>

      {/* Stats */}
      <div className="grid gap-4 grid-cols-1 md:grid-cols-4">
        <Card className="bg-gray-900/50 backdrop-blur-sm border-gray-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-100">{stats.total}</div>
                <div className="text-sm text-gray-400">Total de Regras</div>
              </div>
              <Shield className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900/50 backdrop-blur-sm border-gray-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-red-400">{stats.byPriority.critical}</div>
                <div className="text-sm text-gray-400">Críticas</div>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900/50 backdrop-blur-sm border-gray-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-orange-400">{stats.byPriority.high}</div>
                <div className="text-sm text-gray-400">Alta Prioridade</div>
              </div>
              <Zap className="h-8 w-8 text-orange-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900/50 backdrop-blur-sm border-gray-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-100">
                  {Object.keys(categoryConfig).length}
                </div>
                <div className="text-sm text-gray-400">Categorias</div>
              </div>
              <TrendingUp className="h-8 w-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Buscar regras..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-gray-900/50 border-gray-800 text-gray-100"
          />
        </div>
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-full sm:w-48 bg-gray-900/50 border-gray-800 text-gray-100">
            <SelectValue placeholder="Categoria" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas Categorias</SelectItem>
            {Object.entries(categoryConfig).map(([key, config]) => (
              <SelectItem key={key} value={key}>{config.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={selectedPriority} onValueChange={setSelectedPriority}>
          <SelectTrigger className="w-full sm:w-48 bg-gray-900/50 border-gray-800 text-gray-100">
            <SelectValue placeholder="Prioridade" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas Prioridades</SelectItem>
            {Object.entries(priorityConfig).map(([key, config]) => (
              <SelectItem key={key} value={key}>{config.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Rules List */}
      <div className="space-y-4">
        {filteredRules.length === 0 ? (
          <Card className="bg-gray-900/50 backdrop-blur-sm border-gray-800">
            <CardContent className="py-12 text-center">
              <Search className="h-12 w-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">Nenhuma regra encontrada com os filtros selecionados.</p>
            </CardContent>
          </Card>
        ) : (
          filteredRules.map((rule) => {
            const CategoryIcon = categoryConfig[rule.category]?.icon || Info;
            const PriorityIcon = priorityConfig[rule.priority]?.icon || Info;

            return (
              <Card 
                key={rule.id}
                className="bg-gray-900/70 backdrop-blur-sm border-gray-800 hover:bg-gray-900/90 hover:border-gray-700 transition-all"
              >
                <CardHeader>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <CategoryIcon className="h-5 w-5 text-gray-400" />
                        <CardTitle className="text-lg text-gray-100">{rule.rule}</CardTitle>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={categoryConfig[rule.category]?.color}>
                          {categoryConfig[rule.category]?.label || rule.category}
                        </Badge>
                        <Badge className={priorityConfig[rule.priority]?.color}>
                          <PriorityIcon className="h-3 w-3 mr-1" />
                          {priorityConfig[rule.priority]?.label || rule.priority}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <CardDescription className="text-gray-300">
                    {rule.description}
                  </CardDescription>
                  {rule.affects && rule.affects.length > 0 && (
                    <div className="pt-2 border-t border-gray-800">
                      <p className="text-xs text-gray-500 mb-2">Afeta:</p>
                      <div className="flex flex-wrap gap-2">
                        {rule.affects.map((affect, index) => (
                          <Badge 
                            key={index} 
                            variant="outline" 
                            className="text-xs border-gray-700 text-gray-400"
                          >
                            {affect}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

export default BusinessRulesMap;
